import { useEffect, useRef } from 'react';
import { Tooltip } from 'antd';
import * as echarts from 'echarts';
import { formatCurrency } from '@wavvest/shared-utils';

export type WealthTimeRange = '1W' | '1M' | '3M' | '1Y' | 'YTD' | 'ALL';

export const CHART_LINE_COLOR = '#3A7BD5';

export type PerformanceTrackerLineGraphProps = {
  xValues: Array<number>;
  yValues: Array<number>;
  onCursorSelect?: (x: number, y: number) => void;
  onCursorRelease?: () => void;
  xFormatter?: (value: number) => string;
};

const defaultXFormatter = (v: number) => new Date(v * 1000).toISOString().split('T')[0];

export function PerformanceTrackerLineGraph({
  xValues,
  yValues,
  onCursorSelect,
  onCursorRelease,
  xFormatter = defaultXFormatter,
}: PerformanceTrackerLineGraphProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  // Callers pass new callback functions on every render (and re-render on
  // hover), so read the latest ones through refs. The chart is rebuilt only
  // when the data changes; otherwise hovering would tear it down mid-move.
  const onCursorSelectRef = useRef(onCursorSelect);
  const onCursorReleaseRef = useRef(onCursorRelease);
  const xFormatterRef = useRef(xFormatter);
  onCursorSelectRef.current = onCursorSelect;
  onCursorReleaseRef.current = onCursorRelease;
  xFormatterRef.current = xFormatter;
  // The formatter the x-axis labels were last drawn with.
  const labelledFormatterRef = useRef(xFormatter);

  useEffect(() => {
    if (!chartRef.current) return;
    const observer = new window.ResizeObserver(() => {
      chartInstance.current?.resize();
    });
    observer.observe(chartRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    chartInstance.current = echarts.init(chartRef.current);

    const dataMin = Math.min(...yValues);
    const dataMax = Math.max(...yValues);
    const range = dataMax - dataMin;
    const padding = range === 0 ? Math.abs(dataMin) * 0.05 || 1 : range * 0.05;
    const spansZero = dataMin < 0 && dataMax > 0;
    const yAxisMin = spansZero ? Math.min(0, dataMin - padding) : dataMin - padding;
    const yAxisMax = spansZero ? Math.max(0, dataMax + padding) : dataMax + padding;
    const isSinglePoint = yValues.length === 1;
    const axisLabelColor =
      window.getComputedStyle(chartRef.current).getPropertyValue('--wui-chart-axis-label').trim() ||
      '#6b7280';

    const option = {
      grid: {
        left: '16',
        right: '42',
        bottom: '5',
        top: '5',
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params: { dataIndex: number }[]) {
          const index = params[0].dataIndex;
          const x = xValues[index];
          const y = yValues[index];
          return `${xFormatterRef.current(x)}<br/>Value: ${formatCurrency(y)}`;
        },
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: CHART_LINE_COLOR,
            width: 2,
          },
        },
      },
      xAxis: {
        type: 'category',
        data: xValues.map((x) => xFormatterRef.current(x)),
        boundaryGap: false,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          show: true,
          color: axisLabelColor,
          fontSize: 11,
          interval: xValues.length <= 1 ? 0 : Math.ceil(xValues.length / 6),
        },
        position: 'bottom',
      },
      yAxis: {
        type: 'value',
        show: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          show: true,
          color: axisLabelColor,
          fontSize: 11,
          formatter: function (value: number) {
            return formatCurrency(value);
          },
        },
        splitLine: { show: false },
        min: yAxisMin,
        max: yAxisMax,
        position: 'left',
      },
      series: [
        {
          data: yValues,
          type: 'line',
          smooth: yValues.length > 2,
          showSymbol: isSinglePoint,
          symbol: isSinglePoint ? 'circle' : 'none',
          symbolSize: isSinglePoint ? 10 : 0,
          lineStyle: {
            color: CHART_LINE_COLOR,
            width: 3,
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: CHART_LINE_COLOR },
                { offset: 1, color: `${CHART_LINE_COLOR}0D` },
              ],
            },
          },
        },
      ],
    };

    chartInstance.current.setOption(option);
    labelledFormatterRef.current = xFormatterRef.current;

    chartInstance.current.on('mouseover', (params: { dataIndex: number }) => {
      onCursorSelectRef.current?.(xValues[params.dataIndex], yValues[params.dataIndex]);
    });

    chartInstance.current.on('mousemove', (params: { dataIndex: number }) => {
      onCursorSelectRef.current?.(xValues[params.dataIndex], yValues[params.dataIndex]);
    });

    chartInstance.current.on('mouseout', () => {
      onCursorReleaseRef.current?.();
    });

    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
    };
  }, [xValues, yValues]);

  // A new date format relabels the x-axis in place; the tooltip already reads
  // the latest formatter through its ref.
  useEffect(() => {
    if (!chartInstance.current || labelledFormatterRef.current === xFormatter) {
      return;
    }
    labelledFormatterRef.current = xFormatter;
    chartInstance.current.setOption({ xAxis: { data: xValues.map((x) => xFormatter(x)) } });
  }, [xFormatter, xValues]);

  return (
    <div ref={chartRef} className="performance-chart" style={{ height: '200px', width: '100%' }} />
  );
}

const timestampBounds = (timestamps: number[]): [number, number] =>
  timestamps.reduce<[number, number]>(
    ([earliest, latest], timestamp) => [Math.min(earliest, timestamp), Math.max(latest, timestamp)],
    [Infinity, -Infinity],
  );

export function rangeHasHistory(range: WealthTimeRange, timestamps: number[]): boolean {
  if (range === 'ALL') {
    return true;
  }
  if (timestamps.length < 2) {
    return false;
  }
  const [earliest, latest] = timestampBounds(timestamps);
  const spanDays = (latest - earliest) / 86400;
  // A week of daily points spans 6 days, so "1W" only needs 6.
  const minimumDays: Record<Exclude<WealthTimeRange, 'ALL'>, number> = {
    '1W': 6,
    '1M': 28,
    '3M': 85,
    '1Y': 360,
    YTD: 1,
  };
  return spanDays >= minimumDays[range];
}

export type TimeSelectionSegmentedViewProps = {
  value: WealthTimeRange;
  onChange: (value: WealthTimeRange) => void;
  timestamps?: number[];
};

export function TimeSelectionSegmentedView({
  value,
  onChange,
  timestamps = [],
}: TimeSelectionSegmentedViewProps) {
  const options: Array<WealthTimeRange> = ['1W', '1M', '3M', 'YTD', '1Y', 'ALL'];
  // `timestamps` is the chart currently loaded, which is cut to the selected
  // range. Judge history by the widest span seen so far, so picking 1W doesn't
  // disable 1M/3M/1Y.
  const historyRef = useRef<[number, number] | null>(null);
  if (timestamps.length > 1) {
    const [earliest, latest] = timestampBounds(timestamps);
    const previous = historyRef.current;
    historyRef.current = previous
      ? [Math.min(previous[0], earliest), Math.max(previous[1], latest)]
      : [earliest, latest];
  }
  const history = historyRef.current ?? timestamps;

  return (
    <div className="time-selection" role="group" aria-label="Time range">
      {options.map((option) => {
        // Never disable the range that's currently selected.
        const enabled = option === value || rangeHasHistory(option, history);
        const button = (
          <button
            key={option}
            type="button"
            className={`time-option ${value === option ? 'active' : ''}`}
            aria-pressed={value === option}
            aria-disabled={!enabled}
            disabled={!enabled}
            title={enabled ? undefined : 'Not enough history yet'}
            onClick={() => {
              if (enabled) {
                onChange(option);
              }
            }}
          >
            {option === 'ALL' ? 'All' : option}
          </button>
        );
        return enabled ? (
          button
        ) : (
          <Tooltip key={option} title="Not enough history yet">
            <span>{button}</span>
          </Tooltip>
        );
      })}
    </div>
  );
}
